import React, { useState, useRef, useEffect } from 'react';
import { Upload, Printer, Loader2 } from 'lucide-react';
import type { PrintableRef, Currency } from '@/types';
import PrintableDocument from '@/components/PrintableDocument';
import QuotationsService from '@/api/quotations.service';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

// Update the Customer type to match the schema
type Customer = {
  id: number;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  street: string;
  city: string;
  state: string;
  zipCode?: string; // Optional
  country: string;
  companyName?: string; // Optional
};

export default function DocumentForm({ quotationId }: { quotationId: string }) {
  const [logo, setLogo] = useState<string>();
  const [signature, setSignature] = useState<string>();
  const [currency, setCurrency] = useState<Currency>('USD');
  const [items, setItems] = useState([{ description: '', quantity: 1, unitPrice: 0 }]);
  const [status, setStatus] = useState('pending');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [companyDetails, setCompanyDetails] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    email: '',
    phoneNumber: '',
    logoImg: ''
  });
  const [clientDetails, setClientDetails] = useState<Customer>({
    id: 0,
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    companyName: ''
  });
  const [taxes, setTaxes] = useState([{ taxName: '', taxRate: 0, taxAmount: 0 }]);
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [quotationNumber, setQuotationNumber] = useState('');
  const printableRef = useRef<PrintableRef>(null);
  
  const [isLoading, setIsLoading] = useState(false);

  const { toast } = useToast();
  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    });
  };

  useEffect(() => {
    const fetchQuotation = async () => {
      setIsLoading(true);
      try {
        const response = await QuotationsService.getQuotationById(parseInt(quotationId));
        const { data } = response;
        console.log("getQuotationById data", data);
        if (data.result === 'success') {
          const quotation = data.quotation;
          setCompanyDetails(quotation.business);
          setClientDetails(quotation.customer);
          setItems(quotation.quotationItems);
          setTaxes(quotation.quotationTaxes);
          setNotes(quotation.notes);
          setTerms(quotation.terms);
          setLogo(quotation.logo);
          setSignature(quotation.signature);
          setCurrency(quotation.currency);
          setStatus(quotation.status);
          setQuotationNumber(quotation.quotationNumber);
          setIssueDate(quotation.issueDate);
          setExpiryDate(quotation.expiryDate);
        } else {
          showToastWithTimeout("error", "❌ Fetch Quotation Failed", data.message, 3000);
        }
      } catch (error) {
        console.error(error);
        showToastWithTimeout("error", "❌ Fetch Quotation Failed", "An error occurred while fetching the quotation.", 3000);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuotation();
  }, [quotationId]);

  const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignature(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePrint = () => {
    if (printableRef.current) {
      printableRef.current.print();
    }
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };

  const calculateTotalTax = () => {
    return taxes.reduce((sum, tax) => sum + tax.taxAmount, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTotalTax();
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold capitalize">Edit Quotation</h1>
        <div className="flex gap-4">
          <Button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            disabled={!quotationNumber}
          >
            <Printer size={20} />
            Print
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : 
        companyDetails && ( 
        <>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
              <h2 className="text-lg font-semibold">Company Details</h2>
              <Input
                type="text"
                placeholder="Company Name"
                className="w-full p-2 border rounded"
                value={companyDetails.name}
                readOnly
              />
              <Textarea
                placeholder="Company Address"
                className="w-full p-2 border rounded"
                value={companyDetails.address}
                readOnly
              />
              <Input
                type="email"
                placeholder="Company Email"
                className="w-full p-2 border rounded"
                value={companyDetails.email}
                readOnly
              />
              <Input
                type="tel"
                placeholder="Company Phone"
                className="w-full p-2 border rounded"
                value={companyDetails.phoneNumber}
                readOnly
              />
            </div>

            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Bill To:</h2>
              <Input
                type="text"
                placeholder="First Name"
                className="w-full p-2 border rounded"
                value={clientDetails.firstName}
                readOnly
              />
              <Input
                type="text"
                placeholder="Last Name"
                className="w-full p-2 border rounded"
                value={clientDetails.lastName}
                readOnly
              />
              <Input
                type="text"
                placeholder="Street"
                className="w-full p-2 border rounded"
                value={clientDetails.street}
                readOnly
              />
              <Input
                type="text"
                placeholder="City"
                className="w-full p-2 border rounded"
                value={clientDetails.city}
                readOnly
              />
              <Input
                type="text"
                placeholder="State"
                className="w-full p-2 border rounded"
                value={clientDetails.state}
                readOnly
              />
              <Input
                type="text"
                placeholder="Zip Code"
                className="w-full p-2 border rounded"
                value={clientDetails.zipCode}
                readOnly
              />
              <Input
                type="text"
                placeholder="Country"
                className="w-full p-2 border rounded"
                value={clientDetails.country}
                readOnly
              />
              <Input
                type="text"
                placeholder="Company Name"
                className="w-full p-2 border rounded"
                value={clientDetails.companyName}
                readOnly
              />
              <Input
                type="email"
                placeholder="Client Email"
                className="w-full p-2 border rounded"
                value={clientDetails.email}
                readOnly
              />
              <Input
                type="tel"
                placeholder="Client Phone"
                className="w-full p-2 border rounded"
                value={clientDetails.phone}
                readOnly
              />
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Items</h2>
            {items.map((item, index) => (
              <div key={index} className="flex gap-4 items-center">
                <Input
                  type="text"
                  placeholder="Description"
                  className="flex-1 p-2 border rounded"
                  value={item.description}
                  readOnly
                />
                <Input
                  type="number"
                  placeholder="Quantity"
                  className="w-24 p-2 border rounded"
                  value={item.quantity}
                  readOnly
                />
                <div className="flex items-center">
                  <span className="mx-2">{currency}</span>
                  <Input
                    type="number"
                    placeholder="Price"
                    className="w-32 p-2 border rounded"
                    value={item.unitPrice}
                    readOnly
                  />
                </div>              
              <Button
                type="button"
                onClick={() => {
                  const newItems = items.filter((_: any, i: any) => i !== index);
                  setItems(newItems);
                }}
                className="px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                -
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setItems((prev: any) => [
                    ...prev,
                    { description: '', quantity: 1, unitPrice: 0 },
                  ])
                }}
                className="px-2 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                +
              </Button>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Taxes</h2>
            {taxes.map((tax, index) => (
              <div key={index} className="flex gap-4 items-center">
                <Input
                  type="text"
                  placeholder="Tax Name"
                  className="flex-1 p-2 border rounded"
                  value={tax.taxName}
                  readOnly
                />
                <div className="flex items-center">
                  <Input
                    type="number"
                    placeholder="Tax Rate"
                    className="w-24 p-2 border rounded"
                    value={tax.taxRate}
                    readOnly
                  />
                  <span className="ml-2">%</span>
                </div>
                <div className="flex items-center">
                  <span className="mx-2">{currency}</span>
                  <Input
                    type="number"
                    placeholder="Tax Amount"
                    className="w-32 p-2 border rounded"
                    value={tax.taxAmount}
                    readOnly
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => {
                    const newTaxes = taxes.filter((_: any, i: any) => i !== index);
                    setTaxes(newTaxes);
                  }}
                  className="px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  -
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setTaxes((prev: any) => [
                      ...prev,
                      { taxName: '', taxRate: 0, taxAmount: 0 },
                    ])
                  }}
                  className="px-2 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  +
                </Button>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Additional Details</h2>
            <Textarea
              placeholder="Notes"
              className="w-full p-2 border rounded"
              value={notes}
              readOnly
            />
            <Textarea
              placeholder="Terms and Conditions"
              className="w-full p-2 border rounded"
              value={terms}
              readOnly
            />
          </div>

          <div>
            <label className="block mb-2">
              Upload Signature
              <div className="mt-1 flex items-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleSignatureUpload}
                  className="hidden"
                  id="signature-upload"
                />
                <label
                  htmlFor="signature-upload"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200"
                >
                  <Upload size={20} />
                  Choose Signature
                </label>
              </div>
            </label>
            {signature && (
              <img
                src={signature}
                alt="Signature"
                className="mt-2 max-h-[100px]"
              />
            )}
          </div>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Summary</h2>
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>{currency} {calculateSubtotal().toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Total Tax:</span>
              <span>{currency} {calculateTotalTax().toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold">
              <span>Total:</span>
              <span>{currency} {calculateTotal().toFixed(2)}</span>
            </div>
          </div>

          <div className="hidden">
            <PrintableDocument
              ref={printableRef}
              document={{
                id: quotationId,
                number: quotationNumber,
                type: 'quotation',
                date: new Date().toISOString().split('T')[0],
                currency,
                items,
                status,
                companyDetails,
                clientDetails: {
                  name: `${clientDetails.firstName} ${clientDetails.lastName}`,
                  address: `${clientDetails.street}, ${clientDetails.city}, ${clientDetails.state}, ${clientDetails.country}`,
                  email: clientDetails.email,
                  phoneNumber: clientDetails.phone,
                  companyName: clientDetails.companyName
                },
                notes,
                terms,
                logo,
                signature,
                taxes: taxes.map(tax => ({
                  taxName: tax.taxName,
                  taxRate: tax.taxRate,
                  taxAmount: tax.taxAmount,
                })),
                issueDate: issueDate.split('-').reverse().join('/'),
                expiryDate: expiryDate.split('-').reverse().join('/'),
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}